import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, Layers } from "lucide-react";
import { useFleetModeQuota } from "@/hooks/use-fleet-mode-quota";
import type { DeviceDetailsLookup } from "@/hooks/use-credit-packages-with-devices";

interface FleetPoolPanelProps {
  organizationId: string | null;
  deviceLookup: DeviceDetailsLookup;
}

export const FleetPoolPanel = ({ organizationId, deviceLookup }: FleetPoolPanelProps) => {
  const { isFleetMode, totalAllowed, totalUsed, isLoading } = useFleetModeQuota(organizationId);
  const [expanded, setExpanded] = useState(true);

  const devices = useMemo(() => {
    return Object.entries(deviceLookup)
      .map(([device_id, info]) => ({
        device_id,
        device_name: info.device_name,
        device_serial: info.device_serial,
        images_allowed: info.images_allowed ?? 0,
        images_current: info.images_current ?? 0,
      }))
      .sort((a, b) => b.images_current - a.images_current);
  }, [deviceLookup]);

  if (!isFleetMode || isLoading) return null;

  const pct = totalAllowed > 0 ? Math.min(100, Math.round((totalUsed / totalAllowed) * 100)) : 0;

  return (
    <Card className="border-teal-300 bg-teal-50/40">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-teal-100">
              <Layers className="h-5 w-5 text-teal-700" />
            </div>
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                Fleet Pool
                <Badge className="bg-teal-100 text-teal-800 border-teal-200">Fleet Mode</Badge>
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Image credits are pooled across every device in this organization.
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setExpanded((v) => !v)}>
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="text-xs text-muted-foreground">Pool used</div>
            <div className="text-2xl font-semibold">{totalUsed.toLocaleString()}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Pool size</div>
            <div className="text-2xl font-semibold">{totalAllowed.toLocaleString()}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Utilization</div>
            <div className="text-2xl font-semibold">{pct}%</div>
            <Progress value={pct} className="mt-2" />
          </div>
        </div>

        {expanded && devices.length > 0 && (
          <div className="mt-4 border-t border-teal-200 pt-3">
            <div className="text-xs font-medium text-muted-foreground mb-2">
              Per-device contribution
            </div>
            <div className="max-h-[280px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="py-1 pr-2">Device</th>
                    <th className="py-1 px-2">Used</th>
                    <th className="py-1 px-2">Allowed</th>
                    <th className="py-1 px-2">Share</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((d) => {
                    const share = totalUsed > 0 ? Math.round((d.images_current / totalUsed) * 100) : 0;
                    return (
                      <tr key={d.device_id} className="border-t border-teal-100">
                        <td className="py-1.5 pr-2">
                          <div className="font-medium">
                            {d.device_name || d.device_serial || d.device_id.slice(0, 8)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {d.device_serial ? `SN ${d.device_serial}` : `ID ${d.device_id.slice(0, 8)}…`}
                          </div>
                        </td>
                        <td className="py-1.5 px-2">{d.images_current.toLocaleString()}</td>
                        <td className="py-1.5 px-2">{d.images_allowed.toLocaleString()}</td>
                        <td className="py-1.5 px-2">{share}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FleetPoolPanel;
